import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const BASE = `${config.BASE_URL}/donor-control/donations`;


async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function messageForResponse(res, payload, fallback) {
  if (res.status >= 500) {
    return "Internal server error. Please try again later.";
  }
  const m = payload?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

function buildApiError(res, payload, fallback) {
  const error = new Error(messageForResponse(res, payload, fallback));
  if (payload && typeof payload === "object") {
    error.details = payload.details || {};
    error.reactivated = payload.reactivated;
    error.payload = payload;
  }
  error.status = res.status;
  return error;
}

export const getDonationStats = async () => {
  const res = await authFetch(`${BASE}/stats`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch donation statistics"),
    );
  }
  return data;
};

export const getAllDonations = async () => {
  const res = await authFetch(`${BASE}/all`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(messageForResponse(res, data, "Failed to fetch donations"));
  }
  return data;
};

export const getDonationModes = async () => {
  const res = await authFetch(`${BASE}/modes`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch donation modes"),
    );
  }
  return data;
};

export const getDonationPurposes = async ({ scope } = {}) => {
  const q = scope === "master" ? "?scope=master" : "";
  const res = await authFetch(`${BASE}/purposes${q}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch donation purposes"),
    );
  }
  return data;
};

export const getDonationById = async (id) => {
  const res = await authFetch(
    `${BASE}/${encodeURIComponent(String(id))}`,
    {
      credentials: "include",
      headers: buildAuthHeaders(),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch donation"),
    );
  }
  return data;
};

export const createDonation = async (body, csrfToken) => {
  const res = await authFetch(`${BASE}/create`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(
      csrfToken ? { "X-CSRF-Token": csrfToken } : {},
    ),
    body: JSON.stringify(body),
  });
  const payload = await parseJson(res);
  if (!res.ok) {
    throw buildApiError(res, payload, "Failed to create donation");
  }
  return payload;
};

export const updateDonation = async (id, body, csrfToken) => {
  const res = await authFetch(
    `${BASE}/${encodeURIComponent(String(id))}`,
    {
      method: "PUT",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
      body: JSON.stringify(body),
    },
  );
  const payload = await parseJson(res);
  if (!res.ok) {
    throw buildApiError(res, payload, "Failed to update donation");
  }
  return payload;
};

export const patchDonationRecordStatus = async (id, action, csrfToken) => {
  const res = await authFetch(
    `${BASE}/${encodeURIComponent(String(id))}/record-status`,
    {
      method: "PATCH",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
      body: JSON.stringify({ action }),
    },
  );
  const payload = await parseJson(res);
  if (!res.ok) {
    throw buildApiError(res, payload, "Failed to update donation status");
  }
  return payload;
};

export const bulkSetDonationVerified = async (ids, donationVerified, csrfToken, certificateTemplateId) => {
  const body = {
    ids,
    donation_verified: donationVerified ? 1 : 0,
  };
  if (donationVerified && certificateTemplateId) {
    body.certificate_template_id = Number(certificateTemplateId);
  }
  const res = await authFetch(`${BASE}/bulk-donation-verified`, {
    method: "PATCH",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw buildApiError(res, payload, "Failed to update donation verification");
  return payload;
};

export const createDonationMode = async ({ mode_name, description }, csrfToken) => {
  const res = await authFetch(`${BASE}/modes`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({ mode_name, description }),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw buildApiError(res, payload, "Failed to create donation mode");
  return payload;
};

export const updateDonationMode = async (id, { mode_name, description }, csrfToken) => {
  const res = await authFetch(`${BASE}/modes/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({ mode_name, description }),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw buildApiError(res, payload, "Failed to update donation mode");
  return payload;
};

export const deleteDonationMode = async (id, csrfToken) => {
  const res = await authFetch(`${BASE}/modes/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw buildApiError(res, payload, "Failed to delete donation mode");
  return payload;
};

export const createDonationPurpose = async ({ purpose_name, description }, csrfToken) => {
  const res = await authFetch(`${BASE}/purposes`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({ purpose_name, description }),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw buildApiError(res, payload, "Failed to create donation purpose");
  return payload;
};

export const updateDonationPurpose = async (
  id,
  { purpose_name, description, status_id },
  csrfToken,
) => {
  const res = await authFetch(`${BASE}/purposes/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({
      purpose_name,
      description,
      ...(status_id != null && status_id !== ""
        ? { status_id: Number(status_id) }
        : {}),
    }),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw buildApiError(res, payload, "Failed to update donation purpose");
  return payload;
};

export const deleteDonationPurpose = async (id, csrfToken) => {
  const res = await authFetch(`${BASE}/purposes/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw buildApiError(res, payload, "Failed to delete donation purpose");
  return payload;
};

function parseContentDispositionFilename(header) {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      return star[1].trim();
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || null;
}

function triggerBrowserDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Generate and download 80G certificate PDF for one verified donation. */
export const downloadDonation80gCertificate = async (donationId, csrfToken) => {
  const res = await authFetch(
    `${BASE}/${encodeURIComponent(String(donationId))}/certificate-80g`,
    {
      method: "POST",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken
          ? { "X-CSRF-Token": csrfToken, Accept: "application/pdf" }
          : { Accept: "application/pdf" },
      ),
      body: JSON.stringify({}),
    },
  );

  const contentType = res.headers.get("Content-Type") || "";
  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const data = await parseJson(res);
      throw buildApiError(res, data, "80G certificate download failed");
    }
    const text = await res.text();
    throw new Error(text || "80G certificate download failed");
  }

  const blob = await res.blob();
  const filename =
    parseContentDispositionFilename(res.headers.get("Content-Disposition")) ||
    "80g-certificate.pdf";
  triggerBrowserDownload(blob, filename);
  return { filename };
};
